import React from 'react';
import { Button } from '../components/redesign/ui';
import { ENTITY } from '../lib/statusConfig';
import { Check, CaretRight, WarningCircle, UploadSimple, X, ShoppingBag, MapPin, Clock } from '@phosphor-icons/react';
import {
  COMPLAINT_SUBTYPES,
  DEPARTMENT_OPTIONS,
  ORDER_SOURCE_OPTIONS,
  CUSTOMER_SEGMENT_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  KUWAIT_AREAS,
  CANCELLATION_REASON_OPTIONS,
  ICARRY_APPLICABLE_GROUPS,
  SKU_OPTIONS,
  SKU_IS_PRIVATE_LABEL,
  SKU_APPLICABLE_GROUPS,
  QUANTITY_UNIT_OPTIONS,
  PRODUCT_TYPE_BY_SKU,
} from '../constants/ticketOptions';
import { CHANNEL_SOURCES } from '../utils/constants';

// ── shared token-styled control classes ────────────────────────────────────
const INPUT = 'w-full rounded-field border border-paper-border2 bg-paper px-3 py-2 text-sm text-ink outline-none focus:border-ink';
const INPUT_ERR = 'w-full rounded-field border border-status-escalated bg-paper px-3 py-2 text-sm text-ink outline-none focus:border-ink';
const LABEL = 'mb-1 block text-xs text-ink-faint';
const HEADING = 'font-serif text-base text-ink';
const SECTION_HEAD = 'mb-3 text-[11px] font-semibold uppercase tracking-wide text-ink-faint';
const CHIP_BTN = 'inline-flex items-center gap-1.5 rounded-field border border-paper-border2 px-3 py-2 text-[13px] text-ink hover:bg-paper-muted';
const CHIP_BTN_ACTIVE = 'inline-flex items-center gap-1.5 rounded-field border-[1.5px] border-ink bg-ink/5 px-3 py-2 text-[13px] font-semibold text-ink';

const PLATFORM_ORDER_SOURCES = ['Talabat', 'Zyda', 'Sheeel', 'Snoonu', 'Mashareeb', 'Bylbait', 'One Global', 'Cari'];

// ── presentational helpers (ported from live NewTicket.js) ─────────────────
function Spinner({ className = 'h-4 w-4 text-ink-faint' }) {
  return (
    <svg className={`animate-spin flex-shrink-0 ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
}

function StageBadge({ stage }) {
  const s = (stage || '').toLowerCase();
  const base = 'inline-flex rounded-chip px-1.5 py-0.5 text-[11px] font-medium';
  if (s.includes('closed') || s.includes('won') || s.includes('delivered')) {
    return <span className={`${base} bg-status-resolved/12 text-status-resolved`}>Delivered</span>;
  }
  if (s.includes('cancel') || s.includes('lost')) {
    return <span className={`${base} bg-status-escalated/12 text-status-escalated`}>Cancelled</span>;
  }
  return <span className={`${base} bg-status-under-process/12 text-status-under-process`}>{stage || 'Open'}</span>;
}

function PriorityBadge({ priority }) {
  if (!priority) return null;
  const cfg = {
    critical: 'bg-status-escalated/12 text-status-escalated',
    high:     'bg-status-pending-finance/12 text-status-pending-finance',
    medium:   'bg-status-pending-finance/8 text-status-pending-finance',
    low:      'bg-status-resolved/12 text-status-resolved',
  };
  const cls = cfg[priority] || 'bg-paper-muted text-ink-muted';
  return <span className={`inline-flex rounded-chip px-1.5 py-0.5 text-[11px] font-medium capitalize ${cls}`}>{priority}</span>;
}

function TktChannelBadge({ channel }) {
  if (!channel) return null;
  const lower = channel.toLowerCase();
  const base = 'inline-flex items-center gap-0.5 rounded-chip px-1.5 py-0.5 text-[11px] font-medium';
  if (lower.includes('email')) return <span className={`${base} bg-status-under-process/12 text-status-under-process`}>📧 Email</span>;
  return <span className={`${base} bg-paper-muted text-ink-muted`}>🖥 Portal</span>;
}

function formatTicketDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hrs = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hrs}:${mins}`;
}

function getGroupEmoji(group) {
  const g = (group || '').toLowerCase();
  if (g.includes('delivery') || g.includes('box')) return '🚚';
  if (g.includes('quality') || g.includes('food')) return '💧';
  if (g.includes('order') || g.includes('subscription')) return '📋';
  if (g.includes('payment') || g.includes('refund') || g.includes('billing')) return '💳';
  if (g.includes('driver') || g.includes('staff') || g.includes('dietitian')) return '👤';
  if (g.includes('inquiry')) return '❓';
  return '📝';
}

function getTypeEmoji(type) {
  const t = (type || '').toLowerCase();
  if (t.includes('delay') || t.includes('late') || t.includes('timing')) return '🕐';
  if (t.includes('non-delivery') || t.includes('non delivery') || t.includes('not delivered')) return '📦';
  if (t.includes('address')) return '📍';
  if (t.includes('cancel') || t.includes('cancellation')) return '❌';
  if (t.includes('modif') || t.includes('upgrade') || t.includes('downgrade')) return '✏️';
  if (t.includes('refund') || t.includes('overcharged') || t.includes('pricing') || t.includes('promo') || t.includes('delayed refund')) return '💰';
  if (t.includes('payment') || t.includes('knet') || t.includes('cod') || t.includes('switch')) return '💳';
  if (t.includes('driver') || t.includes('misconduct')) return '🧍';
  if (t.includes('wrong') || t.includes('incorrect') || t.includes('missing') || t.includes('foreign body') ||
      t.includes('contamination') || t.includes('hygiene') || t.includes('poison') || t.includes('allergic') ||
      t.includes('temperature') || t.includes('damaged') || t.includes('short delivery') || t.includes('partial')) return '⚠️';
  if (t.includes('inquiry') || t.includes('status') || t.includes('info') || t.includes('feedback') ||
      t.includes('area') || t.includes('menu') || t.includes('nutrition') || t.includes('general')) return '🔎';
  if (t.includes('exchange')) return '🔄';
  return '📝';
}

const deptDisplay = (slug) => slug === 'fnb' ? 'F&B'
  : slug ? slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : null;

const PRIORITY_LABELS = {
  critical: '🔴 Critical — Requires immediate attention',
  high: '🟠 High',
  medium: '🟡 Medium',
  low: '🟢 Low',
};

// ── entity toggle buttons (locked visual for non-super_admin) ──────────────
const ENTITY_BUTTONS = [
  { id: '1', slug: 'abraaj-water',    label: 'Abraaj Water' },
  { id: '2', slug: 'lofat-lifestyle', label: 'Lofat Lifestyle' },
  { id: '3', slug: 'regency-kuwait',  label: 'Regency Kuwait' },
];

export default function NewTicketView({
  form, set,
  entitySlug, isAbraaj, isLofat, entityLocked,
  loading, errors,
  complaintData, groups, types, uiGroup, autoPriority, routingHint,
  agents,
  customerLookup, ordersState, ticketHistory,
  areaSearch, setAreaSearch,
  photos, setPhotos,
  user,
  onSubmit, onEntitySelect, onGroupSelect, onTypeSelect,
  onMobileChange, onMobileEnter, triggerLookup,
  onSelectCustomer, onSelectOrder, onSkipOrder,
  onCancel,
}) {
  const lookupCustomers = customerLookup?.customers || [];
  const selectedContactIndex = customerLookup?.selectedIndex ?? 0;
  const showTicketHistory = !!form.customer_mobile
    && (ticketHistory?.status === 'loading' || ticketHistory?.status === 'done');
  const showPlatformOrderRef = PLATFORM_ORDER_SOURCES.includes(form.order_source);
  const showIcarry = isAbraaj && ICARRY_APPLICABLE_GROUPS.includes(form.complaint_group);
  const showReplacement = isAbraaj && ['refund_request', 'cancel_order'].includes(form.complaint_group);
  const showSku = isAbraaj && SKU_APPLICABLE_GROUPS.includes(form.complaint_group);
  const showBrandName = !!form.product_sku && SKU_IS_PRIVATE_LABEL[form.product_sku];
  const showAssignment = agents.length > 0 && ['entity_manager', 'super_admin'].includes(user?.role);
  const isCritOrHigh = autoPriority === 'critical' || autoPriority === 'high';

  // Only super_admin sees all 3 buttons; others see just their entity, locked.
  const visibleEntities = entityLocked
    ? ENTITY_BUTTONS.filter((b) => b.id === String(user?.entity_id))
    : ENTITY_BUTTONS;

  return (
    <div className="p-1">
      <div className="overflow-hidden rounded-card border border-paper-border bg-paper">
        {/* ── header ── */}
        <div className="border-b border-paper-border bg-paper-panel px-7 py-6">
          <div className="mb-3 flex items-center gap-2 text-[13px] text-ink-faint">
            <span>Tickets</span><CaretRight size={12} /><span className="text-ink-muted">New Ticket</span>
          </div>
          <div className="flex items-start justify-between gap-5">
            <div>
              <h1 className="mb-1 font-serif text-2xl leading-snug text-ink">New ticket</h1>
              <p className="text-[13px] text-ink-muted">Fields adapt to the selected entity and complaint group.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
              <Button variant="primary" type="submit" form="new-ticket-form" disabled={loading}>
                {loading ? 'Creating…' : 'Create ticket'}
              </Button>
            </div>
          </div>
        </div>

        <form id="new-ticket-form" onSubmit={onSubmit} className="flex flex-col gap-6 p-7">
          {/* ── 1. Entity toggle ── */}
          <section>
            <div className={SECTION_HEAD}>Entity</div>
            <div className="flex flex-wrap gap-2.5">
              {visibleEntities.map((btn) => {
                const cfg = ENTITY[btn.slug];
                const Icon = cfg?.Icon;
                const active = String(btn.id) === String(form.entity_id);
                const cls = active
                  ? `inline-flex items-center gap-2 rounded-field border-[1.5px] border-entity-${cfg?.tw} bg-entity-${cfg?.tw}/8 px-3.5 py-2 text-[13px] font-semibold text-ink`
                  : entityLocked
                    ? 'inline-flex items-center gap-2 rounded-field border border-paper-border2 bg-paper-muted px-3.5 py-2 text-[13px] text-ink-faint cursor-default'
                    : 'inline-flex items-center gap-2 rounded-field border border-paper-border2 bg-paper px-3.5 py-2 text-[13px] text-ink-muted hover:bg-paper-muted';
                return (
                  <button key={btn.id} type="button" disabled={entityLocked}
                    onClick={() => onEntitySelect(btn.id)} className={cls}>
                    {Icon && <Icon size={14} weight="fill" />}{btn.label}
                  </button>
                );
              })}
            </div>
            {errors.entity_id && <p className="mt-2 text-xs text-status-escalated">{errors.entity_id}</p>}
          </section>

          {/* ── 3. Customer card ── */}
          <section className="rounded-card border border-paper-border bg-paper-panel p-5 shadow-soft">
            <div className={`mb-4 ${HEADING}`}>Customer</div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Mobile with lookup + spinner */}
              <div>
                <label className={LABEL}>Mobile Number *</label>
                <div className="relative">
                  <input type="tel" value={form.customer_mobile}
                    onChange={(e) => onMobileChange(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onMobileEnter(); } }}
                    placeholder="+965 XXXX XXXX"
                    className={`${errors.customer_mobile ? INPUT_ERR : INPUT} pr-9`} />
                  {customerLookup?.status === 'loading' && (
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
                      <Spinner className="h-4 w-4 text-status-under-process" />
                    </span>
                  )}
                </div>
                {errors.customer_mobile && <p className="mt-1 text-xs text-status-escalated">{errors.customer_mobile}</p>}
              </div>
              {/* Name */}
              <div>
                <label className={LABEL}>Customer Name</label>
                <input type="text" value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)}
                  placeholder="Full name" className={INPUT} dir="auto" />
              </div>
              {/* Segment */}
              <div>
                <label className={LABEL}>Customer Segment <span className="text-ink-faint">(optional)</span></label>
                <select value={form.customer_segment} onChange={(e) => set('customer_segment', e.target.value)} className={INPUT}>
                  <option value="">— Select segment —</option>
                  {CUSTOMER_SEGMENT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              {/* Area — Abraaj only */}
              {isAbraaj && (
                <div>
                  <label className={LABEL}>Customer Area <span className="text-ink-faint">(optional)</span></label>
                  <input type="text" value={areaSearch || form.customer_area}
                    onChange={(e) => { setAreaSearch(e.target.value); if (e.target.value === '') set('customer_area', ''); }}
                    onBlur={(e) => {
                      const m = KUWAIT_AREAS.find((a) => a.toLowerCase() === e.target.value.toLowerCase());
                      if (m) { set('customer_area', m); setAreaSearch(m); }
                    }}
                    list="kuwait-areas-list" placeholder="Type to search area…" className={INPUT} />
                  <datalist id="kuwait-areas-list">
                    {KUWAIT_AREAS.filter((a) => !areaSearch || a.toLowerCase().includes(areaSearch.toLowerCase())).map((a) => (
                      <option key={a} value={a} />
                    ))}
                  </datalist>
                  {form.customer_area && <p className="mt-1 text-xs text-status-resolved">✓ {form.customer_area}</p>}
                </div>
              )}
              {/* Loading banner */}
              {customerLookup?.status === 'loading' && (
                <div className="sm:col-span-2 flex items-center gap-2 rounded-field border border-paper-border bg-paper-muted px-3 py-2.5 text-sm text-ink-muted">
                  <Spinner />Looking up customer…
                </div>
              )}
              {/* HubSpot match rows */}
              {customerLookup?.status === 'found' && lookupCustomers.length > 0 && (
                <div className="sm:col-span-2 overflow-hidden rounded-card border border-status-resolved/40 bg-status-resolved/8">
                  <div className="flex items-center gap-2 border-b border-status-resolved/30 bg-status-resolved/12 px-4 py-2.5">
                    <Check size={14} weight="bold" className="text-status-resolved" />
                    <span className="text-sm font-semibold text-status-resolved">
                      {!entitySlug
                        ? `${lookupCustomers.length} contact${lookupCustomers.length > 1 ? 's' : ''} found — select one to set entity`
                        : lookupCustomers.length === 1 ? 'Customer found' : `${lookupCustomers.length} contacts found — select one`}
                    </span>
                    <span className="text-xs text-ink-faint">via HubSpot</span>
                  </div>
                  <div className="divide-y divide-status-resolved/20">
                    {lookupCustomers.map((c, i) => {
                      const selected = i === selectedContactIndex;
                      return (
                        <button key={c.contactId || i} type="button"
                          onClick={() => onSelectCustomer(i)}
                          className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition ${selected ? 'border-l-[3px] border-status-under-process bg-status-under-process/8' : 'border-l-[3px] border-transparent hover:bg-status-resolved/12'}`}>
                          <span className={`flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 ${selected ? 'border-status-under-process bg-status-under-process' : 'border-paper-border2 bg-paper'}`}>
                            {selected && <span className="h-1.5 w-1.5 rounded-full bg-paper" />}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-semibold text-ink" dir="auto">{c.name || '—'}</span>
                              {c.arabicName && c.arabicName !== c.name && <span className="text-xs text-ink-faint" dir="auto">{c.arabicName}</span>}
                              {!entitySlug && c.entityLabel && (
                                <span className="rounded-chip bg-paper-muted px-1.5 py-0.5 text-[11px] font-medium text-ink-muted">{c.entityLabel}</span>
                              )}
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-ink-faint">
                              {c.mobile && <span>{c.mobile}</span>}{c.email && <span>{c.email}</span>}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Orders panel — Abraaj only */}
                  {isAbraaj && ordersState?.status === 'loading' && (
                    <div className="flex items-center gap-2 border-t border-status-resolved/30 px-4 py-2.5 text-xs text-status-resolved">
                      <Spinner className="h-3.5 w-3.5 text-status-resolved" />Loading orders…
                    </div>
                  )}
                  {isAbraaj && ordersState?.status === 'done' && (
                    <div className="border-t border-status-resolved/30">
                      <div className="flex items-center justify-between bg-status-resolved/8 px-4 py-2.5">
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-status-resolved">
                          <ShoppingBag size={14} />
                          {ordersState.orders.length > 0
                            ? `Last ${ordersState.orders.length} Order${ordersState.orders.length > 1 ? 's' : ''} — select the one related to this complaint`
                            : 'No orders found for this contact'}
                        </span>
                        {ordersState.selectedDealId && (
                          <button type="button" onClick={onSkipOrder} className="text-xs font-medium text-ink-muted underline hover:text-status-escalated">
                            Skip — not related to an order
                          </button>
                        )}
                      </div>
                      {ordersState.orders.length > 0 && (
                        <div className="divide-y divide-status-resolved/12">
                          {ordersState.orders.map((ord) => {
                            const selected = ordersState.selectedDealId === ord.dealId;
                            const cat = [ord.custCat1, ord.custCat2].filter(Boolean).join('/');
                            return (
                              <button key={ord.dealId} type="button" onClick={() => onSelectOrder(ord)}
                                className={`flex w-full items-start gap-3 px-4 py-2.5 text-left transition ${selected ? 'border-l-[3px] border-status-under-process bg-status-under-process/8' : 'border-l-[3px] border-transparent hover:bg-status-resolved/12'}`}>
                                <span className={`mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 ${selected ? 'border-status-under-process bg-status-under-process' : 'border-paper-border2 bg-paper'}`}>
                                  {selected && <span className="h-1.5 w-1.5 rounded-full bg-paper" />}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="truncate text-sm font-semibold text-ink">{ord.dealName || `Deal #${ord.dealId}`}</span>
                                    <StageBadge stage={ord.stage} />
                                  </div>
                                  <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-ink-faint">
                                    {ord.date && <span>{new Date(ord.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                                    {ord.amount && parseFloat(ord.amount) > 0 && <span className="font-medium text-ink-muted">KD {parseFloat(ord.amount).toFixed(3)}</span>}
                                    {ord.channel && <span>{ord.channel}</span>}
                                    {ord.orderSource && <span>{ord.orderSource}</span>}
                                    {ord.address && <span>{ord.address}</span>}
                                    {cat && <span className="rounded-chip bg-status-resolved/12 px-1.5 py-0.5 font-medium text-status-resolved">{cat}</span>}
                                  </div>
                                  {ord.deliveryAddress && (
                                    <div className="mt-1 flex items-start gap-1 text-xs leading-relaxed text-ink-faint">
                                      <MapPin size={12} className="mt-0.5 flex-none" />
                                      <span>{ord.deliveryAddress.replace(/\r\n/g, ' · ').replace(/\n/g, ' · ')}</span>
                                    </div>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {ordersState.orders.length > 0 && !ordersState.selectedDealId && (
                        <div className="border-t border-status-resolved/20 px-4 py-2">
                          <button type="button" onClick={onSkipOrder} className="text-xs font-medium text-ink-muted underline hover:text-ink">
                            Skip — not related to an order
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {customerLookup?.status === 'not_found' && (
                <div className="sm:col-span-2 flex items-center gap-2 rounded-field border border-paper-border bg-paper-muted px-3 py-2.5 text-sm text-ink-muted">
                  <WarningCircle size={16} className="text-ink-faint" />New customer — no previous records found
                </div>
              )}
              {/* Email */}
              <div>
                <label className={LABEL}>Email Address</label>
                <input type="email" value={form.customer_email} onChange={(e) => set('customer_email', e.target.value)}
                  placeholder="customer@email.com" className={INPUT} />
              </div>
              {/* Channel Source (required) */}
              <div>
                <label className={LABEL}>Channel Source *</label>
                <select value={form.channel_source} onChange={(e) => set('channel_source', e.target.value)}
                  className={errors.channel_source ? INPUT_ERR : INPUT}>
                  <option value="">Select channel…</option>
                  {CHANNEL_SOURCES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {errors.channel_source && <p className="mt-1 text-xs text-status-escalated">{errors.channel_source}</p>}
              </div>
            </div>
          </section>

          {/* ── Previous Tickets panel (below Customer card) ── */}
          {showTicketHistory && (
            <section className="rounded-card border border-paper-border bg-paper-panel p-5 shadow-soft">
              <div className="mb-4 flex items-center gap-2">
                <Clock size={16} className="text-ink-faint" />
                <span className={HEADING}>Previous Tickets</span>
                {ticketHistory.status === 'loading' && <Spinner />}
                {ticketHistory.status === 'done' && ticketHistory.tickets.length > 0 && (
                  <span className="text-xs font-normal text-ink-faint">({ticketHistory.tickets.length})</span>
                )}
              </div>
              {ticketHistory.status === 'loading' && <p className="text-sm text-ink-faint">Loading ticket history…</p>}
              {ticketHistory.status === 'done' && ticketHistory.tickets.length === 0 && (
                <p className="text-sm text-ink-faint">No previous tickets found for this customer.</p>
              )}
              {ticketHistory.status === 'done' && ticketHistory.tickets.length > 0 && (
                <div className="divide-y divide-paper-border">
                  {ticketHistory.tickets.map((t) => (
                    <div key={t.id} className="py-2.5 first:pt-0 last:pb-0">
                      <div className="flex flex-wrap items-start gap-2">
                        <span className="flex-none text-sm font-bold text-status-under-process">{t.ticketRef}</span>
                        <span className="min-w-0 flex-1 text-sm leading-snug text-ink" dir="auto">{t.subject}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {(t.complaintGroup || t.complaintType) && (
                          <span className="text-xs text-ink-faint">{[t.complaintGroup, t.complaintType].filter(Boolean).join(' · ')}</span>
                        )}
                        <PriorityBadge priority={t.priority} />
                        <TktChannelBadge channel={t.channelSource} />
                        {t.createdAt && <span className="text-xs text-ink-faint">{formatTicketDate(t.createdAt)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ── 4. Complaint classification ── */}
          <section className="rounded-card border border-paper-border bg-paper-panel p-5 shadow-soft">
            <div className={`mb-4 ${HEADING}`}>Complaint Classification</div>
            <div className="space-y-5">
              <div>
                <label className={LABEL}>Complaint Group *</label>
                {!entitySlug ? (
                  <p className="text-xs text-ink-faint">Select an entity first</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {groups.map((g) => {
                      const active = uiGroup === g;
                      return (
                        <button key={g} type="button" onClick={() => onGroupSelect(g)} className={active ? CHIP_BTN_ACTIVE : CHIP_BTN}>
                          <span>{getGroupEmoji(g)}</span>{g}
                        </button>
                      );
                    })}
                  </div>
                )}
                {errors.complaint_group && <p className="mt-1 text-xs text-status-escalated">{errors.complaint_group}</p>}
              </div>
              {uiGroup && types.length > 0 && (
                <div>
                  <label className={LABEL}>Complaint Type *</label>
                  <div className="flex flex-wrap gap-2">
                    {types.map((t) => {
                      const active = form.complaint_type === t;
                      return (
                        <button key={t} type="button" onClick={() => onTypeSelect(t)} className={active ? CHIP_BTN_ACTIVE : CHIP_BTN}>
                          <span>{getTypeEmoji(t)}</span>{t}
                        </button>
                      );
                    })}
                  </div>
                  {errors.complaint_type && <p className="mt-1 text-xs text-status-escalated">{errors.complaint_type}</p>}
                  {routingHint && (
                    <div className="mt-2 flex items-center gap-2 rounded-field border border-status-under-process/30 bg-status-under-process/8 px-3 py-2 text-sm text-status-under-process">
                      <CaretRight size={14} />
                      <span>This complaint will be auto-routed to: <b>{deptDisplay(routingHint)}</b></span>
                    </div>
                  )}
                </div>
              )}
              {/* Subtype */}
              {form.complaint_group && (() => {
                const opts = COMPLAINT_SUBTYPES[form.complaint_group]?.[form.complaint_type] || [];
                return opts.length > 0 ? (
                  <div>
                    <label className={LABEL}>Sub-type <span className="text-ink-faint">(optional)</span></label>
                    <select value={form.complaint_subtype} onChange={(e) => set('complaint_subtype', e.target.value)} className={INPUT}>
                      <option value="">— Select sub-type —</option>
                      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className={LABEL}>Sub-type <span className="text-ink-faint">(optional)</span></label>
                    <input type="text" value={form.complaint_subtype} onChange={(e) => set('complaint_subtype', e.target.value)}
                      placeholder="Sub-type (optional)" className={INPUT} />
                  </div>
                );
              })()}
              {/* Cancellation reason */}
              {form.complaint_group === 'cancel_order' && (
                <div>
                  <label className={LABEL}>Cancellation Reason <span className="text-status-escalated">*</span></label>
                  <select value={form.cancellation_reason} onChange={(e) => set('cancellation_reason', e.target.value)} required
                    className={errors.cancellation_reason ? INPUT_ERR : INPUT}>
                    <option value="">— Select cancellation reason —</option>
                    {CANCELLATION_REASON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  {errors.cancellation_reason && <p className="mt-1 text-xs text-status-escalated">{errors.cancellation_reason}</p>}
                </div>
              )}
              {/* Auto-priority banner */}
              {autoPriority && (
                <div className={`flex items-center gap-2 rounded-field px-3 py-2 text-sm ${
                  autoPriority === 'critical' ? 'border border-status-escalated/30 bg-status-escalated/8 text-status-escalated' :
                  autoPriority === 'high'     ? 'border border-status-pending-finance/30 bg-status-pending-finance/8 text-status-pending-finance' :
                  autoPriority === 'medium'   ? 'border border-status-pending-finance/20 bg-status-pending-finance/5 text-status-pending-finance' :
                                                'border border-status-resolved/30 bg-status-resolved/8 text-status-resolved'
                }`}>
                  <WarningCircle size={16} weight="fill" />
                  <span><b>Auto Priority:</b> {PRIORITY_LABELS[autoPriority]}</span>
                </div>
              )}
            </div>
          </section>

          {/* ── 5. Ticket details ── */}
          <section className="rounded-card border border-paper-border bg-paper-panel p-5 shadow-soft">
            <div className={`mb-4 ${HEADING}`}>Ticket Details</div>
            <div className="space-y-4">
              <div>
                <label className={LABEL}>Subject *</label>
                <input type="text" value={form.subject} onChange={(e) => set('subject', e.target.value)}
                  placeholder="Brief description of the complaint"
                  className={errors.subject ? INPUT_ERR : INPUT} dir="auto" />
                {errors.subject && <p className="mt-1 text-xs text-status-escalated">{errors.subject}</p>}
              </div>
              <div>
                <label className={LABEL}>Description</label>
                <textarea rows={4} value={form.complaint_description}
                  onChange={(e) => set('complaint_description', e.target.value)}
                  placeholder="Detailed description of the complaint…"
                  className={`${INPUT} resize-none`} dir="auto" />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={LABEL}>
                    Order Reference
                    {ordersState?.selectedDealId && <span className="ml-2 text-xs font-normal text-status-under-process">linked to deal</span>}
                  </label>
                  <input type="text" value={form.sap_order_reference}
                    onChange={(e) => set('sap_order_reference', e.target.value)}
                    placeholder="Order # or Deal ID" className={INPUT} />
                </div>
                {isLofat && (
                  <div>
                    <label className={LABEL}>Meal Date</label>
                    <input type="date" value={form.meal_date} onChange={(e) => set('meal_date', e.target.value)} className={INPUT} />
                  </div>
                )}
              </div>
              {showIcarry && (
                <div>
                  <label className={LABEL}>iCarry Reference <span className="text-ink-faint">(optional — log if handing off to iCarry)</span></label>
                  <input type="text" value={form.icarry_reference} onChange={(e) => set('icarry_reference', e.target.value)}
                    placeholder="iCarry job or ticket reference number" className={INPUT} />
                </div>
              )}
              <div>
                <label className={LABEL}>Order Source <span className="text-ink-faint">(optional)</span></label>
                <select value={form.order_source} onChange={(e) => set('order_source', e.target.value)} className={INPUT}>
                  <option value="">— Select order source —</option>
                  {ORDER_SOURCE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              {showPlatformOrderRef && (
                <div>
                  <label className={LABEL}>Platform Order Reference <span className="text-ink-faint">(optional)</span></label>
                  <input type="text" value={form.platform_order_reference}
                    onChange={(e) => set('platform_order_reference', e.target.value)}
                    placeholder={`Order ID from ${form.order_source}`} className={INPUT} />
                </div>
              )}
              {/* SKU block — moved above Product Type so picking a SKU auto-fills
                  Product Type in place (reads top-down: pick SKU → see auto-fill). */}
              {showSku && (
                <div className="space-y-3 rounded-field border border-paper-border2 bg-paper p-3">
                  <div>
                    <label className={LABEL}>Product SKU <span className="text-ink-faint">(optional)</span></label>
                    <select
                      value={form.product_sku}
                      onChange={(e) => {
                        const nextSku = e.target.value;
                        set('product_sku', nextSku);
                        if (!SKU_IS_PRIVATE_LABEL[nextSku]) set('product_sku_brand', '');
                        // Auto-fill product_type from the SKU mapping. Overwrite:
                        // last SKU pick wins, agent's manual multi-select edits
                        // (added AFTER pick) are preserved until the next SKU change.
                        // Empty SKU clears the auto-fill; unmapped SKU (shouldn't
                        // happen — all 47 map) falls back to empty.
                        set('product_type', nextSku ? (PRODUCT_TYPE_BY_SKU[nextSku] || '') : '');
                      }}
                      className={INPUT}
                    >
                      <option value="">— Select product —</option>
                      {SKU_OPTIONS.map((cat) => (
                        <optgroup key={cat.category} label={cat.category}>
                          {cat.items.map((it) => (
                            <option key={it.sku} value={it.sku}>{it.sku}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  {showBrandName && (
                    <div>
                      <label className={LABEL}>Brand / Client Name <span className="text-ink-faint">(private label — enter the client's brand)</span></label>
                      <input type="text" value={form.product_sku_brand}
                        onChange={(e) => set('product_sku_brand', e.target.value)}
                        placeholder="Client brand name" className={INPUT} dir="auto" />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={LABEL}>Quantity <span className="text-ink-faint">(optional)</span></label>
                      <input type="number" min="0" step="1"
                        value={form.product_quantity}
                        onChange={(e) => set('product_quantity', e.target.value)}
                        placeholder="e.g. 5" className={INPUT} />
                    </div>
                    <div>
                      <label className={LABEL}>Unit <span className="text-ink-faint">(optional)</span></label>
                      <select value={form.product_quantity_unit}
                        onChange={(e) => set('product_quantity_unit', e.target.value)}
                        className={INPUT}>
                        <option value="">— Select —</option>
                        {QUANTITY_UNIT_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}
              {isAbraaj && (
                <div>
                  <label className={LABEL}>Product Type <span className="text-ink-faint">(optional — select all that apply; auto-fills from SKU)</span></label>
                  <div className="flex flex-col gap-1.5 rounded-field border border-paper-border2 bg-paper p-3">
                    {PRODUCT_TYPE_OPTIONS.map((opt) => {
                      const selected = new Set((form.product_type || '').split(', ').filter(Boolean));
                      const checked = selected.has(opt);
                      return (
                        <label key={opt} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                          <input type="checkbox" checked={checked}
                            onChange={() => {
                              const next = new Set((form.product_type || '').split(', ').filter(Boolean));
                              if (next.has(opt)) next.delete(opt); else next.add(opt);
                              set('product_type', PRODUCT_TYPE_OPTIONS.filter((o) => next.has(o)).join(', '));
                            }} />
                          {opt}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {isAbraaj && (
                <div>
                  <label className={LABEL}>SAP Route <span className="text-ink-faint">(optional)</span></label>
                  <input type="text" value={form.sap_route} onChange={(e) => set('sap_route', e.target.value)}
                    placeholder="e.g. R5G09 or RPETA12" className={INPUT} />
                </div>
              )}
              {showReplacement && (
                <div>
                  <label className={LABEL}>Replacement Order <span className="text-ink-faint">(Abraaj refund/cancel — optional)</span></label>
                  <input type="text" value={form.replacement_order_reference || ''}
                    onChange={(e) => set('replacement_order_reference', e.target.value)}
                    placeholder="New SAP order number" className={INPUT} />
                </div>
              )}
              {/* Photos */}
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-xs text-ink-faint">Attach Photos</label>
                  {photos.length > 0 && <span className="text-xs text-ink-faint">{photos.length}/5 photos</span>}
                </div>
                {photos.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {photos.map((file, idx) => (
                      <div key={idx} className="relative h-16 w-16 flex-none">
                        <img src={URL.createObjectURL(file)} alt={file.name}
                          className="h-16 w-16 rounded-field border border-paper-border object-cover" />
                        <button type="button"
                          onClick={() => setPhotos(photos.filter((_, i) => i !== idx))}
                          className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-status-escalated text-paper hover:bg-status-escalated/90"
                          title="Remove photo">
                          <X size={10} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {photos.length < 5 && (
                  <label className="flex cursor-pointer items-center gap-2 rounded-field border border-dashed border-paper-border2 px-3 py-2 hover:border-status-under-process hover:bg-status-under-process/5">
                    <UploadSimple size={16} className="text-ink-faint" />
                    <span className="text-sm text-ink-muted">
                      {photos.length === 0
                        ? 'Click to upload photos (JPG, PNG, WEBP — max 5)'
                        : `Add more (${5 - photos.length} remaining)`}
                    </span>
                    <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp" multiple
                      onChange={(e) => {
                        const incoming = Array.from(e.target.files || []);
                        setPhotos([...photos, ...incoming]);
                        e.target.value = '';
                      }} />
                  </label>
                )}
              </div>
              {isCritOrHigh && (
                <div>
                  <label className={LABEL}>Escalation Notes</label>
                  <textarea rows={2} value={form.escalation_reason}
                    onChange={(e) => set('escalation_reason', e.target.value)}
                    placeholder="Add escalation notes for this priority issue…"
                    className="w-full resize-none rounded-field border border-status-pending-finance/30 bg-status-pending-finance/5 px-3 py-2 text-sm text-ink outline-none focus:border-status-pending-finance" dir="auto" />
                </div>
              )}
            </div>
          </section>

          {/* ── 6. Assignment ── */}
          {showAssignment && (
            <section className="rounded-card border border-paper-border bg-paper-panel p-5 shadow-soft">
              <div className={`mb-4 ${HEADING}`}>Assignment</div>
              <div className="space-y-4">
                <div>
                  <label className={LABEL}>Assign To</label>
                  <select value={form.assigned_to} onChange={(e) => set('assigned_to', e.target.value)} className={INPUT}>
                    <option value="">Unassigned</option>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.role.replace('_', ' ')})</option>)}
                  </select>
                </div>
                {isAbraaj && (
                  <div>
                    <label className={LABEL}>Department Assigned <span className="text-ink-faint">(optional)</span></label>
                    <select value={form.department_assigned} onChange={(e) => set('department_assigned', e.target.value)} className={INPUT}>
                      <option value="">— Auto-routed —</option>
                      {DEPARTMENT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ── 7. Submit row ── */}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={loading}>{loading ? 'Creating…' : 'Create Ticket'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
